import { useEffect, useRef, type FocusEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { cn } from './cn';
import { Button } from './primitives';

/**
 * The design's overlay dialog — `payModalOpen` / `newLeadOpen` in the mockup.
 *
 * Rendered through a portal so it escapes the shell's stacking and overflow
 * contexts; a dialog clipped by a scrolling table is the classic version of
 * this bug. Closes on Escape and on backdrop click, and restores focus to
 * whatever opened it.
 */
export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Wider variant for the multi-column forms. */
  size?: 'md' | 'lg';
  /**
   * Makes the dialog a real `<form>`, so ENTER SUBMITS IT.
   *
   * Without this the body and footer are separate `<div>`s, Enter in a text
   * input does nothing, and every dialog needs a mouse. Passing `onSubmit` wraps
   * body + footer in a form and gives the browser its native behaviour back:
   * Enter in any single-line field submits, Escape still cancels.
   *
   * The primary action must then be `type="submit"` — `Button` defaults to
   * `type="button"` precisely so it never submits by accident.
   */
  onSubmit?: () => void;
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  onSubmit,
}: ModalProps): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusTo = useRef<Element | null>(null);

  /**
   * `onClose` behind a ref, so the effect below does NOT depend on it.
   *
   * THIS IS THE FOCUS-STEALING BUG. Callers pass an inline arrow
   * (`onClose={() => { reset(); onClose(); }}`), which is a new function on
   * every render. With `onClose` in the dependency array the effect re-ran on
   * every keystroke — and because it calls `panelRef.current.focus()`, focus was
   * yanked out of the input after each character. Keep this ref; putting
   * `onClose` back in the deps reintroduces it.
   */
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // Depends ONLY on `open`, so it runs once per open/close — not per render.
  useEffect(() => {
    if (!open) return;

    restoreFocusTo.current = document.activeElement;

    /**
     * Focus the first real field, falling back to the panel.
     *
     * This effect runs AFTER React has applied `autoFocus`, so focusing the
     * panel unconditionally would immediately undo it and leave the user
     * pressing Tab before they can type.
     */
    const firstField = panelRef.current?.querySelector<HTMLElement>(
      'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])',
    );
    (firstField ?? panelRef.current)?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKeyDown);

    // The page behind must not scroll while a dialog is up.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      (restoreFocusTo.current as HTMLElement | null)?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/45 p-4 pt-[6vh]"
      onMouseDown={(event) => {
        // Only a click that STARTS on the backdrop closes — otherwise a drag
        // that ends outside the panel (selecting text in a field) would close it.
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cn(
          'w-full rounded-[12px] border border-neutral-300 bg-surface-raised shadow-[0_18px_50px_rgba(0,0,0,.22)] outline-none',
          size === 'lg' ? 'max-w-[760px]' : 'max-w-[520px]',
        )}
      >
        <div className="border-b border-neutral-200 px-5 py-4">
          <div className="flex items-start gap-3">
            <div>
              <h2 className="text-[16px] font-bold leading-none">{title}</h2>
              {description !== undefined && (
                <p className="mt-2 text-[12px] leading-[1.5] text-neutral-600">{description}</p>
              )}
            </div>
            <Button
              variant="ghost"
              className="ml-auto -mr-2 -mt-1 px-2 py-1"
              onClick={onClose}
              aria-label="Close"
            >
              ✕
            </Button>
          </div>
        </div>

        <FormOrDiv onSubmit={onSubmit}>
          <div className="px-5 py-4">{children}</div>

          {footer !== undefined && (
            <div className="flex items-center justify-end gap-2 border-t border-neutral-200 px-5 py-4">
              {footer}
            </div>
          )}
        </FormOrDiv>
      </div>
    </div>,
    document.body,
  );
}

/**
 * A `<form>` when the dialog is submittable, a plain `<div>` otherwise.
 *
 * Split out so the markup stays identical either way — a confirmation dialog
 * with nothing to submit should not emit a stray form element.
 */
function FormOrDiv({
  onSubmit,
  children,
}: {
  onSubmit?: () => void;
  children: ReactNode;
}): JSX.Element {
  if (onSubmit === undefined) return <>{children}</>;
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      {children}
    </form>
  );
}

/* ── form controls, styled to the design's inputs ──────────────────────────── */

/**
 * Select the whole field on focus — for a number field pre-filled with a
 * value staff commonly REPLACE rather than edit (a price, a suggested
 * amount, a seat count). Without it, a click lands the cursor at the end of
 * the existing text, so the first keystroke inserts rather than replaces —
 * typing "5" into a field already showing "0" produces "05", not "5", and an
 * HTML number input's own value sanitisation does not fix this: "05" is
 * already a syntactically valid number per the HTML spec, so nothing ever
 * normalises it away.
 */
export function selectAllOnFocus(e: FocusEvent<HTMLInputElement>): void {
  e.target.select();
}

const FIELD =
  'w-full rounded-[8px] border border-neutral-300 bg-surface-raised px-3 py-[9px] text-[13px] leading-none ' +
  'placeholder:text-neutral-500 focus:border-ink focus:outline-none disabled:opacity-55';

export function Field({
  label,
  hint,
  error,
  required,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-[6px] block text-[11px] font-semibold uppercase leading-none tracking-caps text-neutral-600">
        {label}
        {required === true && <span className="text-brand"> *</span>}
      </span>
      {children}
      {error !== undefined ? (
        <span className="mt-[5px] block text-[11px] leading-[1.4] text-danger-ink">{error}</span>
      ) : (
        hint !== undefined && (
          <span className="mt-[5px] block text-[11px] leading-[1.4] text-neutral-600">{hint}</span>
        )
      )}
    </label>
  );
}

export function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>): JSX.Element {
  return <input {...props} className={cn(FIELD, props.className)} />;
}

export function TextArea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>): JSX.Element {
  return <textarea {...props} className={cn(FIELD, 'min-h-[84px] leading-[1.5]', props.className)} />;
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>): JSX.Element {
  return <select {...props} className={cn(FIELD, 'pr-8', props.className)} />;
}
